/**
 * PdfUploadDialog
 *
 * Modal dialog for uploading a PDF file and creating a note from it.
 * Mirrors the NiceGUI show_pdf_upload_window() in import_export_dialogs.py.
 *
 * Fields:
 *   - Title (required — defaults to filename)
 *   - Tags  (optional, uses TagSelector)
 *   - Summary language (optional — triggers AI summarization via Gemini)
 *   - PDF file input (accept=".pdf")
 *
 * On submit calls POST /api/v1/notes/file (multipart/form-data).
 */
import { useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Tag, uploadPdfNote } from '../api/notes'
import { SUMMARY_LANGUAGES } from '../constants/languages'
import TagSelector from './TagSelector'

interface PdfUploadDialogProps {
  onClose: () => void
  onSuccess?: () => void
}

export default function PdfUploadDialog({ onClose, onSuccess }: PdfUploadDialogProps) {
  const navigate = useNavigate()

  // Form state
  const [title, setTitle] = useState('')
  const [selectedTags, setSelectedTags] = useState<Tag[]>([])
  const [summaryLanguage, setSummaryLanguage] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')

  // UI state
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null
    if (!f) return
    setFile(f)
    setFileName(f.name)
    // Auto-fill title from filename (strip .pdf extension)
    if (!title) {
      setTitle(f.name.replace(/\.pdf$/i, ''))
    }
  }, [title])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const f = e.dataTransfer.files[0]
    if (f && f.type === 'application/pdf') {
      setFile(f)
      setFileName(f.name)
      if (!title) setTitle(f.name.replace(/\.pdf$/i, ''))
    }
  }, [title])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) { setError('Please select a PDF file.'); return }
    if (!title.trim()) { setError('Please enter a title.'); return }

    setError(null)
    setIsUploading(true)
    try {
      const created = await uploadPdfNote(
        file,
        title.trim(),
        selectedTags,
        summaryLanguage || undefined,
      )
      onSuccess?.()
      onClose()
      navigate(`/notes/${created.id}`)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      if (msg === 'note_limit_reached') {
        setError('Note limit reached. Upgrade to premium to create more notes.')
      } else if (msg === 'storage_limit_reached') {
        setError('Storage limit reached.')
      } else if (msg === 'no_tokens') {
        setError('AI token limit reached. You have no remaining AI tokens to generate a summary.')
      } else if (msg?.includes('PDF processing is unavailable')) {
        setError('PDF processing is currently unavailable on the server.')
      } else {
        setError(msg || 'Upload failed. Please try again.')
      }
    } finally {
      setIsUploading(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  const dialog = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div
        data-testid="pdf-upload-dialog"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {/* PDF icon */}
            <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 7V3.5L18.5 9H13zm-4.5 5.5h1a2 2 0 010 4h-.5v1.5H8v-5.5zm1 2.5v1h.5a.5.5 0 000-1h-.5zM12 14.5h1.5a.5.5 0 010 1H13v.5h.5a.5.5 0 010 1H13v1h-1v-3.5zm4 0H17a.5.5 0 010 1h-.5v.5H17a.5.5 0 010 1h-.5V18h-1v-3.5z"/>
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Upload PDF Note</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="pdf-upload-close"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Error */}
          {error && (
            <div
              data-testid="pdf-upload-error"
              className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label htmlFor="pdf-title" className="block text-sm font-medium text-gray-700 mb-1">
              Note title <span className="text-red-500">*</span>
            </label>
            <input
              id="pdf-title"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Enter a title for the note"
              data-testid="pdf-upload-title"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <TagSelector
              selectedTags={selectedTags}
              onChange={setSelectedTags}
              placeholder="Select tags…"
            />
          </div>

          {/* Summary language */}
          <div>
            <label htmlFor="pdf-lang" className="block text-sm font-medium text-gray-700 mb-1">
              AI summary language{' '}
              <span className="text-gray-400 font-normal">(optional — generates summary instead of raw text)</span>
            </label>
            <select
              id="pdf-lang"
              value={summaryLanguage}
              onChange={e => setSummaryLanguage(e.target.value)}
              data-testid="pdf-upload-language"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
            >
              <option value="">No AI summary (keep extracted text)</option>
              {SUMMARY_LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>{lang.label}</option>
              ))}
            </select>
          </div>

          {/* File drop zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PDF file <span className="text-red-500">*</span>
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              data-testid="pdf-upload-dropzone"
              className={`
                border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
                ${file ? 'border-primary bg-primary-50' : 'border-gray-300 hover:border-primary hover:bg-gray-50'}
              `}
            >
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"/>
                    <path d="M14 2v6h6"/>
                  </svg>
                  <p className="text-sm font-medium text-gray-900 truncate max-w-full px-4">{fileName}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setFile(null); setFileName(''); if (fileInputRef.current) fileInputRef.current.value = '' }}
                    className="text-xs text-red-500 hover:text-red-700 mt-1"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium text-primary">Click to browse</span> or drag &amp; drop
                  </p>
                  <p className="text-xs text-gray-400">PDF files only</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
                data-testid="pdf-upload-file-input"
                className="hidden"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            data-testid="pdf-upload-cancel"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form=""
            onClick={handleSubmit}
            disabled={!file || !title.trim() || isUploading}
            data-testid="pdf-upload-submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span data-testid="pdf-upload-status-text">
                  {summaryLanguage ? 'Summarizing…' : 'Uploading…'}
                </span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(dialog, document.body)
}




